import type { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { Badge } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { TelemetryUserInteraction } from '@ctw/shared/context/telemetry/user-interactions';
import { tw } from '@ctw/shared/utils/tailwind';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';

interface ViewCCDAButtonProps {
  ccdaTitle: string;
  binary: BinaryModel;
  preferred: boolean;
  overrideDateDisplay?: string;
  telemetryInteractionName: TelemetryUserInteraction;
  telemetryObfuscatedIdentifier: string;
  telemetryContext?: TelemetryContext;
}

export const ViewCCDAButton = ({
  ccdaTitle,
  binary,
  preferred,
  overrideDateDisplay,
  telemetryInteractionName,
  telemetryObfuscatedIdentifier,
  telemetryContext,
}: ViewCCDAButtonProps) => {
  const telemetry = useTelemetry();
  const { openResourceModal } = useResourceModal({
    layout: {
      mode: 'full',
      overflowBehavior: 'hidden',
      fullbleed: true,
      showCloseButton: false,
    },
  });

  const date =
    overrideDateDisplay ?? (binary.createdAt ? format(new Date(binary.createdAt), 'M/d/yyyy') : '');
  const time = binary.createdAt ? format(new Date(binary.createdAt), 'h:mma') : '';

  return (
    <Button
      type="button"
      variant="link"
      className={tw`group w-full`}
      telemetryInteractionName={telemetryInteractionName}
      telemetryObfuscatedIdentifier={telemetryObfuscatedIdentifier}
      telemetryContext={telemetryContext}
      onClick={() => openResourceModal({ telemetry, model: binary })}
    >
      <div
        className={tw`flex items-start justify-between gap-3 rounded-lg border border-divider-main bg-white p-3 text-left text-background-inverse`}
      >
        <div className={tw`flex gap-5`}>
          <div>
            <div className={tw`whitespace-nowrap font-medium`}>{time}</div>
            <div className={tw`whitespace-nowrap`}>{date}</div>
          </div>
          <div>
            <div className={tw`font-medium group-hover:underline`}>{ccdaTitle}</div>
            {preferred && <Badge variant="meta" content="Notes" />}
          </div>
        </div>
        <FontAwesomeIcon
          icon={faFileLines}
          className={tw`h-5 w-5 justify-self-end text-content-subtle`}
        />
      </div>
    </Button>
  );
};
